import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useMatchLargeViewport } from '@avensia/excite';
import { ArrowDown, SortIcon } from '../../components/icons';
import { Theme } from 'webapp/theme';
import { Button } from 'webapp/components/button';
import { createFlipYDecorator, useOnClickOutside } from '@avensia/excite-ui';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { darkGrey } from 'webapp/shared/sharedTheme';
import { Sort } from './Sort';
import { useRef } from 'react';
import { NnTestIdProps } from 'webapp/shared/test-ids';

type FocusIndex = number | null;

type PropType = {
  totalProductCount: number;
  sortOptions: NordicNest.Api.SelectOptionApiModel[];
  alignBottom: boolean | undefined;
  setOptionsIsOpen: (isOpen: boolean) => void;
  sortOptionsIsOpen: boolean;
  onChangeSort: (value: string, text: string) => void;
  sortRef: React.RefObject<HTMLDivElement | null>;
  sortOption: string | undefined;
};

export const CountAndSort = (props: PropType) => {
  const isDesktop = useMatchLargeViewport();
  const toggleSort = useRef<HTMLButtonElement>(null);
  const [currentFocus, setCurrentFocus] = React.useState<FocusIndex>(null);
  const refList = props.sortOptions.map(() => React.createRef<HTMLLIElement>());

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cycle up or down. Also start over if we're outside the list bounds.
    if (props.sortOptionsIsOpen) {
      if (e.code === 'Escape') {
        props.setOptionsIsOpen(false);
        setCurrentFocus(null);

        if (toggleSort && toggleSort.current) {
          toggleSort.current?.focus();
        }
      }

      if (isDesktop && e.code === 'Tab') {
        props.setOptionsIsOpen(false);
        setCurrentFocus(null);
      }

      if (e.code === 'ArrowDown' || e.code === 'ArrowRight') {
        e.preventDefault();
        setCurrentFocus(
          currentFocus === null ? 0 : currentFocus === props.sortOptions.length - 1 ? 0 : currentFocus + 1,
        );
      }
      if (e.code === 'ArrowUp' || e.code === 'ArrowLeft') {
        e.preventDefault();
        setCurrentFocus(
          currentFocus === null
            ? props.sortOptions.length - 1
            : currentFocus === 0
            ? props.sortOptions.length - 1
            : currentFocus - 1,
        );
      }
    }
  };

  useOnClickOutside(props.sortRef, () => props.setOptionsIsOpen(false), props.sortOptionsIsOpen);

  React.useEffect(() => {
    if (currentFocus !== null) {
      refList[currentFocus].current?.focus();
    }
  }, [currentFocus]);

  React.useEffect(() => {
    if (props.sortOptionsIsOpen) {
      setCurrentFocus(null);
    }
  }, [props.sortOptionsIsOpen]);

  return (
    <Base css={props.alignBottom && alignmentDecorator}>
      <TotalCount {...NnTestIdProps.PlpProductCount}>
        {props.totalProductCount} {translate(t => t.productListing.sortBy)}
      </TotalCount>
      <div ref={props.sortRef} onKeyDown={e => handleKeyDown(e)}>
        <SortButton
          ref={toggleSort}
          onClick={() => props.setOptionsIsOpen(!props.sortOptionsIsOpen)}
          as={Theme.Transparent}
          disableRippleEffect
          aria-expanded={`${props.sortOptionsIsOpen ? 'true' : 'false'}`}
          aria-label={translate(t => t.productListing.sortButtonText)}
          role="combobox"
          aria-haspopup="listbox"
          aria-controls="listbox"
          {...NnTestIdProps.PlpSortButton}
        >
          <SortIcon />
          {props.sortOption ? props.sortOption : props.sortOptions[0].text}
          <ArrowDown css={createFlipYDecorator(props.sortOptionsIsOpen)} />
        </SortButton>
        {isDesktop && (
          <Sort
            sortOptions={props.sortOptions}
            onChangeSort={props.onChangeSort}
            isOpen={props.sortOptionsIsOpen}
            setIsOpen={props.setOptionsIsOpen}
            refList={refList}
          />
        )}
      </div>
    </Base>
  );
};

const Base = styled.div(
  createMinLargeViewportDecorator({
    justifyContent: 'flex-end',
    padding: { y: t => t.sharedSpacing.xxs },
  }),
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: { left: t => t.sharedSpacing.xs, right: t => t.sharedSpacing.xxs },
    flexGrow: 1,
    position: 'relative',
    gap: t => t.sharedSpacing.m,
    gridArea: 'sort',
  },
);

const alignmentDecorator = styled(
  createMinLargeViewportDecorator({
    alignSelf: 'flex-end',
  }),
);

const TotalCount = styled.span(
  createMaxSmallViewportDecorator({
    flexGrow: 1,
  }),
  {
    fontSize: t => t.fontSize.xxs,
    color: darkGrey,
  },
);

const SortButton = styled(Button, {
  padding: { x: 0, y: t => t.sharedSpacing.xxs },
  fontSize: t => t.fontSize.xxs,
  gap: t => t.sharedSpacing.xxs,
  fontWeight: t => t.fontWeight?.bold,
  lineHeight: 'normal',
  textTransform: 'unset',
});
